import React, { FC, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import { Box, Text, Range, Input, Slider } from '@wix/design-system';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  ACTIONS,
  useExploreContext,
} from '@components/Contexts/ExploreContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { updateQueryParams } from '@utils/route';
import { MIN_PRICE } from '@utils/constants';
import useFormattedCurrency from '@components/Hooks/useFormattedCurrency';
import { useBi } from '@components/Hooks/useBi';
import { useShallowRouter } from '@components/Hooks/useRouter';
import FilterPopover from '../FilterPopover';

const Budget: FC = () => {
  const { t } = useTranslation();
  const { sessionId } = useBiSessionContext();
  const {
    dispatch,
    state: {
      selectedPricing,
      entry,
      origin,
      currentPage,
      userCurrency,
      maxPrice,
    },
  } = useExploreContext();
  const { navigate, router } = useShallowRouter();
  const logger = useBi();
  const [value, setValue] = useState({ ...selectedPricing });
  const [hasMax, setHasMax] = useState(!!selectedPricing.max);
  const { currencyFormatter, symbol: currencySymbol } = useFormattedCurrency({
    currency: userCurrency,
  });

  useEffect(() => {
    setValue({ ...selectedPricing });
    setHasMax(!!selectedPricing.max);

    if (!selectedPricing.max) {
      setValue({ min: selectedPricing.min, max: maxPrice });
    }
  }, [selectedPricing, maxPrice]);

  const activeFilterCount = useMemo(() => {
    if (value.min !== MIN_PRICE || hasMax) {
      return 1;
    }
    return 0;
  }, [value.min, hasMax]);

  function onApplyBudget() {
    const shouldFlipMinAndMax = value.min! > value.max!;
    const updatedBudgetLimits = shouldFlipMinAndMax
      ? { min: value.max, max: value.min }
      : { min: value.min, max: hasMax ? value.max : undefined };

    navigate(updateQueryParams(updatedBudgetLimits, router));

    dispatch({
      type: ACTIONS.FILTER_POPOVER_STATE,
      shouldOpen: false,
    });

    logger.report(
      exploreResultsPageGenericClick({
        clickType: 'apply budget filter',
        sessionId,
        origin,
        entry,
        data: JSON.stringify({ value, currentPage }),
        exploreVersion: 'V2',
      }),
    );
  }

  return (
    <FilterPopover
      numSelectedServices={activeFilterCount}
      element={t('explore-filter-budget')}
      panel="budget"
      minWidth={255}
      onClear={() => {
        logger.report(
          exploreResultsPageGenericClick({
            clickType: 'clear all budget filters',
            sessionId,
            origin,
            entry,
            exploreVersion: 'V2',
            data: JSON.stringify({
              currentPage,
            }),
          }),
        );
        setValue({ min: MIN_PRICE, max: maxPrice });
        setHasMax(false);
      }}
      onApply={() => onApplyBudget()}
    >
      <Box paddingTop="6px" direction="vertical" dataHook="budget-filter">
        <Text secondary size="small" weight="thin">
          {t('budget-filter-price-range')}
        </Text>
        <Slider
          onChange={(val: any) => {
            if (val[1] === maxPrice) {
              setHasMax(false);
            } else {
              setHasMax(true);
            }
            setValue({ min: val[0], max: val[1] });
          }}
          displayTooltip={false}
          displayMarks={false}
          min={MIN_PRICE}
          max={maxPrice}
          value={[value.min!, value.max!]}
        />
        <Box align="space-between">
          <Text secondary light size="tiny" weight="bold">
            {currencyFormatter(MIN_PRICE)}
          </Text>
          <Text secondary light size="tiny" weight="bold">
            {currencyFormatter(maxPrice)}+
          </Text>
        </Box>
        <Box maxWidth="fit-content" paddingTop="24px" paddingBottom="6px">
          <Range>
            <Input
              dataHook="min-price-input"
              prefix={<Input.Affix>{currencySymbol}</Input.Affix>}
              value={value.min}
              onChange={(e: any) => {
                setValue({ min: Number(e.target.value), max: value.max });
              }}
              type="number"
            />

            <Input
              dataHook="max-price-input"
              prefix={<Input.Affix>{currencySymbol}</Input.Affix>}
              value={!hasMax ? `${value.max}+` : value.max}
              maxLength={10}
              onChange={(e: any) => {
                if (_.isFinite(_.toNumber(e.target.value))) {
                  setHasMax(true);
                  setValue({ min: value.min, max: Number(e.target.value) });
                }
              }}
            />
          </Range>
        </Box>
      </Box>
    </FilterPopover>
  );
};

export default Budget;
